import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface BrandingColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  textPrimary: string;
  textSecondary: string;
}

export interface BrandingTypography {
  fontFamily: string;
  headingWeight: string;
  bodyWeight: string;
}

interface BrandingState {
  logoUrl: string | null;
  thumbnailUrl: string | null;
  colors: BrandingColors | null;
  typography: BrandingTypography | null;
  appName: string;
  pwaShortName: string;
  isLoaded: boolean;
  tenantSlug: string | null;
  tenantId: string | null;

  setBranding: (branding: Partial<BrandingState>) => void;
  clearBranding: () => void;
  applyTheme: () => void;
}

const DEFAULT_COLORS: BrandingColors = {
  primary: '#1e3a8a',
  secondary: '#3b82f6',
  accent: '#10b981',
  background: '#f9fafb',
  surface: '#ffffff',
  textPrimary: '#111827',
  textSecondary: '#6b7280',
};

const DEFAULT_TYPOGRAPHY: BrandingTypography = {
  fontFamily: 'Inter',
  headingWeight: '600',
  bodyWeight: '400',
};

// Font configurations for dynamic loading
const FONT_CONFIGS: Record<string, { googleUrl: string | null; stack: string }> = {
  inter: {
    googleUrl: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
    stack: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  roboto: {
    googleUrl: 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap',
    stack: "'Roboto', Arial, sans-serif",
  },
  'open-sans': {
    googleUrl: 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;500;600;700&display=swap',
    stack: "'Open Sans', Arial, sans-serif",
  },
  lato: {
    googleUrl: 'https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap',
    stack: "'Lato', Arial, sans-serif",
  },
  'source-sans': {
    googleUrl: 'https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@400;600;700&display=swap',
    stack: "'Source Sans Pro', Arial, sans-serif",
  },
  system: {
    googleUrl: null,
    stack: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
};

function loadGoogleFont(fontFamily: string): void {
  const config = FONT_CONFIGS[fontFamily];
  if (!config?.googleUrl) return;

  // Check if this font is already loaded
  const existingLink = document.querySelector(`link[href="${config.googleUrl}"]`);
  if (existingLink) return;

  // Remove previously loaded brand font links (to avoid accumulation)
  document.querySelectorAll('link[data-brand-font]').forEach((el) => el.remove());

  const link = document.createElement('link');
  link.href = config.googleUrl;
  link.rel = 'stylesheet';
  link.setAttribute('data-brand-font', 'true');
  document.head.appendChild(link);
}

function applyBrandingToDOM(colors: BrandingColors | null, typography: BrandingTypography | null): void {
  const root = document.documentElement;

  // Apply colors
  const effectiveColors = colors || DEFAULT_COLORS;
  root.style.setProperty('--color-brand-primary', effectiveColors.primary);
  root.style.setProperty('--color-brand-secondary', effectiveColors.secondary);
  root.style.setProperty('--color-brand-accent', effectiveColors.accent);
  root.style.setProperty('--color-brand-background', effectiveColors.background);
  root.style.setProperty('--color-brand-surface', effectiveColors.surface);
  root.style.setProperty('--color-brand-text-primary', effectiveColors.textPrimary);
  root.style.setProperty('--color-brand-text-secondary', effectiveColors.textSecondary);

  // Apply typography
  const effectiveTypography = typography || DEFAULT_TYPOGRAPHY;
  const fontConfig = FONT_CONFIGS[effectiveTypography.fontFamily.toLowerCase()] || FONT_CONFIGS.system;

  // Load Google Font if needed
  if (effectiveTypography.fontFamily.toLowerCase() !== 'system') {
    loadGoogleFont(effectiveTypography.fontFamily.toLowerCase());
  }

  root.style.setProperty('--font-brand', fontConfig.stack);
  root.style.setProperty('--font-heading-weight', effectiveTypography.headingWeight);
  root.style.setProperty('--font-body-weight', effectiveTypography.bodyWeight);
}

/**
 * Update PWA manifest link to use tenant-specific manifest
 * Note: Existing PWA installations won't see these changes until reinstalled
 */
function updatePWAManifest(tenantSlug: string | null): void {
  const API_BASE = import.meta.env.VITE_API_URL || '';
  let manifestLink = document.querySelector('link[rel="manifest"]');

  if (!manifestLink) {
    manifestLink = document.createElement('link');
    manifestLink.setAttribute('rel', 'manifest');
    document.head.appendChild(manifestLink);
  }

  if (tenantSlug) {
    // Use tenant-specific dynamic manifest with origin for cross-origin support
    const origin = encodeURIComponent(window.location.origin);
    manifestLink.setAttribute('href', `${API_BASE}/api/public/branding/${tenantSlug}/manifest.webmanifest?origin=${origin}`);
  } else {
    // Fall back to static manifest
    manifestLink.setAttribute('href', '/manifest.webmanifest');
  }

  // Update theme-color meta tag
  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (themeColorMeta) {
    const state = useBrandingStore.getState();
    themeColorMeta.setAttribute('content', state.colors?.primary || DEFAULT_COLORS.primary);
  }
}

export const useBrandingStore = create<BrandingState>()(
  persist(
    (set, get) => ({
      logoUrl: null,
      thumbnailUrl: null,
      colors: null,
      typography: null,
      appName: 'Command Bridge',
      pwaShortName: 'CmdBridge',
      isLoaded: false,
      tenantSlug: null,
      tenantId: null,

      setBranding: (branding) => {
        set({ ...branding, isLoaded: true });
        const state = get();
        state.applyTheme();
        updatePWAManifest(branding.tenantSlug || state.tenantSlug);
      },

      clearBranding: () => {
        set({
          logoUrl: null,
          thumbnailUrl: null,
          colors: null,
          typography: null,
          appName: 'Command Bridge',
          pwaShortName: 'CmdBridge',
          isLoaded: false,
          tenantSlug: null,
          tenantId: null,
        });
        applyBrandingToDOM(null, null);
        updatePWAManifest(null);
      },

      applyTheme: () => {
        const { colors, typography } = get();
        applyBrandingToDOM(colors, typography);
      },
    }),
    {
      name: 'branding-storage',
      partialize: (state) => ({
        logoUrl: state.logoUrl,
        thumbnailUrl: state.thumbnailUrl,
        colors: state.colors,
        typography: state.typography,
        appName: state.appName,
        pwaShortName: state.pwaShortName,
        tenantSlug: state.tenantSlug,
        tenantId: state.tenantId,
      }),
      onRehydrateStorage: () => (state) => {
        // Apply branding when store is rehydrated from localStorage
        if (state) {
          // Migration: Clear stale or invalid logo URLs
          // Valid formats:
          // - Absolute URLs starting with http:// or https://
          // - Relative proxy URLs starting with /api/
          // Invalid formats that need clearing:
          // - Signed S3 URLs containing linodeobjects.com (they expire)
          // - Raw storage keys (e.g., "tenant-id/branding/123_logo.png")
          if (state.logoUrl) {
            const isAbsoluteUrl = state.logoUrl.startsWith('http://') || state.logoUrl.startsWith('https://');
            const isValidRelativeUrl = state.logoUrl.startsWith('/api/');
            const isSignedS3Url = state.logoUrl.includes('linodeobjects.com');

            if (isSignedS3Url || (!isAbsoluteUrl && !isValidRelativeUrl)) {
              state.logoUrl = null;
              state.thumbnailUrl = null;
            }
          }
          applyBrandingToDOM(state.colors, state.typography);
          updatePWAManifest(state.tenantSlug);
        }
      },
    }
  )
);

// Export defaults for use in components
export { DEFAULT_COLORS, DEFAULT_TYPOGRAPHY, FONT_CONFIGS };
